import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import {
  AiOutlineCheckCircle,
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineLoading3Quarters,
} from "react-icons/ai";
import * as authApi from "../../api/authApi";
import { getPostLoginPath } from "../../utils/auth";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginFromResponse } = useAuthStore();
  const redirectAfterLogin = location.state?.from?.pathname;
  /* Lời nhắn từ màn khác chuyển sang (hiện chỉ có màn Đăng ký). Đọc một lần lúc
     mount: giữ trong state để còn xoá được khi người dùng bắt đầu đăng nhập,
     thay vì đọc thẳng `location.state` ở mỗi lần render. */
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [formData, setFormData] = useState({
    email: location.state?.registeredEmail || "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (name, value, currentErrors = errors) => {
    const newErrors = { ...currentErrors };
    if (name === "email") {
      if (!value) newErrors.email = "Email là bắt buộc";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) newErrors.email = "Email không hợp lệ";
      else delete newErrors.email;
    }
    if (name === "password") {
      if (!value) newErrors.password = "Mật khẩu là bắt buộc";
      else if (value.length < 6) newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
      else delete newErrors.password;
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => validateField(name, value, prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    let newErrors = validateField("email", formData.email, {});
    newErrors = validateField("password", formData.password, newErrors);
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    // Đã bắt tay vào đăng nhập thì lời chúc mừng đăng ký xong không còn chỗ đứng —
    // nhất là khi lát nữa có lỗi hiện ra, hai khung xanh/đỏ cạnh nhau rất khó hiểu
    setNotice("");
    setIsLoading(true);

    try {
      const { data } = await authApi.login({
        email: formData.email,
        password: formData.password,
      });
      if (!data.success) {
        throw new Error(data.message || "Đăng nhập thất bại. Sai email hoặc mật khẩu.");
      }
      const user = loginFromResponse(data, formData.email);
      navigate(getPostLoginPath(user.role, redirectAfterLogin));
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 text-sm border rounded focus:outline-none transition-colors ${
      touched[field] && errors[field]
        ? "border-red-400 bg-red-50 focus:border-red-400"
        : "border-gray-300 dark:border-white/15 bg-gray-50 dark:bg-white/5 focus:border-gray-500 focus:bg-white dark:focus:bg-[#161a22]"
    }`;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero section with background */}
      <div
        className="flex-1 flex flex-col items-center pt-10 pb-20 relative"
        style={{
          background: "linear-gradient(rgba(10,20,50,0.72), rgba(10,20,60,0.80)), url('/images/tournaments/action-1.jpg') center/cover no-repeat",
          backgroundColor: "#0d1b3e",
        }}
      >
        {/* Logo BTMS */}
        <div className="text-center mb-5">
          <div style={{ fontSize: 42, fontWeight: 900, color: "#fff", fontStyle: "italic", letterSpacing: -1.5, lineHeight: 1, textTransform: "uppercase" }}>
            btms<span style={{ color: "#e8471a" }}>.</span>
          </div>
          <p style={{ fontSize: 13, color: "#ccc", marginTop: 6 }}>
            Chào mừng bạn đến với nền tảng tỉ số trực tuyến <strong style={{ color: "#fff", fontStyle: "italic", textTransform: "uppercase" }}>btms</strong>.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-[#161a22] rounded-lg w-full max-w-sm mx-4" style={{ padding: "28px 28px 24px" }}>
          {/* Log in section */}
          <h2 style={{ fontSize: 13, fontWeight: 700, color: "#1a2a4a", textTransform: "uppercase", letterSpacing: "1.5px", fontStyle: "italic", marginBottom: 16 }}>
            Đăng nhập.
          </h2>

          {notice && (
            <div className="mb-4 p-3 rounded text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 flex items-start gap-2">
              <AiOutlineCheckCircle size={14} className="mt-0.5 shrink-0" />
              <span>{notice}</span>
            </div>
          )}

          {errors.submit && (
            <div className="mb-4 p-3 rounded text-xs text-red-600 bg-red-50 border border-red-200">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label className="block text-xs text-gray-600 dark:text-white/70 mb-1">Địa chỉ E-mail</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                className={inputClass("email")}
                placeholder="Nhập địa chỉ email"
              />
              {touched.email && errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div className="mb-2">
              <label className="block text-xs text-gray-600 dark:text-white/70 mb-1">Mật khẩu</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => setTouched((p) => ({ ...p, password: true }))}
                  className={`${inputClass("password")} pr-9`}
                  placeholder="Nhập mật khẩu"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <AiOutlineEyeInvisible size={16} /> : <AiOutlineEye size={16} />}
                </button>
              </div>
              {touched.password && errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>

            <div className="text-right mb-4">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Khôi phục mật khẩu tại đây
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 rounded-full text-sm font-semibold text-white transition-colors"
              style={{ background: isLoading ? "#9ca3af" : "#1a2a4a" }}
            >
              {isLoading ? (
                <>
                  <AiOutlineLoading3Quarters className="animate-spin" size={14} />
                  Đang xử lý...
                </>
              ) : (
                "Đăng nhập"
              )}
            </button>
          </form>

          {/* Divider */}
          <hr className="my-5 border-gray-200 dark:border-white/10" />

          {/* Sign up section */}
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#1a2a4a", textTransform: "uppercase", letterSpacing: "1.5px", fontStyle: "italic", marginBottom: 8 }}>
            Bạn chưa có tài khoản?
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Đăng ký ngay để cập nhật tỉ số trực tiếp từ <strong style={{ fontStyle: "italic", color: "#111", textTransform: "uppercase" }}>btms</strong>.
          </p>
          <button
            type="button"
            onClick={() => navigate("/register")}
            className="px-6 py-2 rounded-full text-sm font-semibold text-white transition-colors"
            style={{ background: "#1a2a4a" }}
            onMouseOver={(e) => (e.target.style.background = "#243660")}
            onMouseOut={(e) => (e.target.style.background = "#1a2a4a")}
          >
            Đăng ký ngay
          </button>

          <div className="text-right mt-4">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Quay lại trang sự kiện
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: "#111d30", padding: "32px 40px 0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1.4fr", gap: 24, maxWidth: 960, margin: "0 auto" }}>
          {/* Logo Footer */}
          <div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", fontStyle: "italic", letterSpacing: -1, textTransform: "uppercase" }}>
              btms<span style={{ color: "#e8471a" }}>.</span>
            </div>
          </div>

          {/* News */}
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 12 }}>Tin tức</h4>
            <a href="/news"
              style={{ fontSize: 12.5, color: "#8a99b5", textDecoration: "none", display: "block" }}>
              🌐 Tin tức BTMS
            </a>
          </div>

          {/* Legal */}
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 12 }}>Pháp lý</h4>
            {/* Chưa có trang đích — xem chú thích cùng khối ở ForgotPasswordPage */}
            {["Điều khoản & Điều kiện", "Chính sách bảo mật", "Chính sách Cookie"].map((item) => (
              <button
                key={item}
                type="button"
                style={{ fontSize: 12.5, color: "#8a99b5", textDecoration: "none", display: "block", marginBottom: 6, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
              >
                {item}
              </button>
            ))}
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 12 }}>Liên hệ</h4>
            <p style={{ fontSize: 12.5, color: "#8a99b5", marginBottom: 12 }}>✉️ support@btms.vn</p>
            <p style={{ fontSize: 12.5, color: "#8a99b5", lineHeight: 1.6 }}>
              Hệ thống quản lý giải đấu Bi-a BTMS
            </p>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #1e2d4a", marginTop: 28, padding: "14px 0", textAlign: "center" }}>
          <p style={{ fontSize: 11.5, color: "#6b7280" }}>
            Nền tảng cập nhật tỉ số trực tiếp <strong style={{ fontStyle: "italic", color: "#8a99b5", textTransform: "uppercase" }}>btms</strong>.<br />
            Bản quyền © BTMS. Bảo lưu mọi quyền.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;